import React, { useEffect, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Card,
  Group,
  Center,
  Box,
  useMantineColorScheme,
  useMantineTheme,
  Loader
} from '@mantine/core';
import { IconCheck, IconArrowRight, IconCreditCard } from '@tabler/icons-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { mercadoPagoService } from '../services/mercadoPagoService';
import PageTransition from '../components/animations/PageTransition';

const PaymentSuccessPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const [processingStatus, setProcessingStatus] = useState<string>('processing');

  useEffect(() => {
    const processPayment = async () => {
      try {
        // Primero intentar obtener datos del pago directo (Checkout Transparente)
        const directPaymentData = sessionStorage.getItem('payment_success_data');
        
        if (directPaymentData) {
          // Pago directo exitoso
          const paymentData = JSON.parse(directPaymentData);
          
          setPaymentInfo({
            success: true,
            status: 'approved',
            message: '¡Pago procesado exitosamente! Tu suscripción se ha activado.',
            paymentId: paymentData.paymentId,
            session: {
              planName: `Plan ${paymentData.planId}`,
              amount: paymentData.amount,
              subscriptionId: paymentData.subscriptionId
            }
          });
          
          setProcessingStatus('success');
          
          notifications.show({
            color: 'green',
            title: '¡Pago exitoso!',
            message: 'Tu suscripción se ha activado correctamente.',
            autoClose: false
          });
          
          // Limpiar datos después de usarlos
          sessionStorage.removeItem('payment_success_data');
          
        } else {
          // Fallback: pago clásico con redirección
          const paymentSession = mercadoPagoService.getPaymentSession();
          const result = await mercadoPagoService.processPaymentResult(searchParams);
          
          setPaymentInfo({
            ...result,
            session: paymentSession
          });

          if (result.success && result.status !== 'unknown') {
            setProcessingStatus(result.status === 'approved' ? 'success' : 'pending');
            
            notifications.show({
              color: result.status === 'approved' ? 'green' : 'blue',
              title: result.status === 'approved' ? '¡Pago exitoso!' : 'Pago en procesamiento',
              message: result.message,
              autoClose: false
            });
          } else {
            // Estado desconocido
            setProcessingStatus('error');
            setPaymentInfo({
              success: false,
              status: 'unknown',
              message: 'No se pudo verificar el estado del pago. Por favor, verifica tu suscripción en tu perfil.',
              session: null
            });
          }
        }
        
      } catch (error) {
        console.error('Error procesando resultado de pago:', error);
        setProcessingStatus('error');
      } finally {
        setLoading(false);
      }
    };

    processPayment();
  }, [searchParams]);

  const handleContinue = () => {
    // Limpiar todas las sesiones de pago
    mercadoPagoService.clearPaymentSession();
    sessionStorage.removeItem('payment_success_data');
    
    // Redirigir según el estado
    if (processingStatus === 'success') {
      navigate('/dashboard');
    } else {
      navigate('/settings/billing');
    }
  };

  const handleRetry = () => {
    mercadoPagoService.clearPaymentSession();
    sessionStorage.removeItem('payment_success_data');
    navigate('/mejorarplan');
  };

  if (loading) {
    return (
      <Container size="md" py="xl">
        <Center style={{ minHeight: '60vh' }}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text size="lg">Procesando resultado del pago...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  const getStatusIcon = () => {
    switch (processingStatus) {
      case 'success':
        return (
          <Box
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: theme.colors.green[6],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}
          >
            <IconCheck size={40} color="white" />
          </Box>
        );
      case 'pending':
        return (
          <Box
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: theme.colors.blue[6],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}
          >
            <IconCreditCard size={40} color="white" />
          </Box>
        );
      default:
        return null;
    }
  };

  const getStatusTitle = () => {
    switch (processingStatus) {
      case 'success':
        return '¡Pago confirmado!';
      case 'pending':
        return 'Pago en proceso';
      default:
        return 'Procesando...';
    }
  };

  const getStatusMessage = () => {
    switch (processingStatus) {
      case 'success':
        return 'Tu suscripción se ha activado correctamente. Ya puedes acceder a todas las funciones de tu plan.';
      case 'pending':
        return 'Tu pago está siendo procesado. Te notificaremos por email cuando se complete la confirmación.';
      default:
        return paymentInfo?.message || 'Verificando el estado de tu pago...';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
    }}>
      <PageTransition type="medical" duration={600}>
        <Container size="md" py="xl">
          <Center style={{ minHeight: '70vh' }}>
            <Card
              shadow="md"
              radius="lg"
              p="xl"
              style={{
                maxWidth: 500,
                width: '100%',
                backgroundColor: colorScheme === 'dark' ? theme.colors.dark[6] : 'white',
                border: `1px solid ${colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]}`
              }}
            >
              <Stack align="center" gap="lg">
                {getStatusIcon()}
                
                <Stack align="center" gap="xs">
                  <Title 
                    order={2} 
                    ta="center"
                    c={colorScheme === 'dark' ? 'white' : 'dark'}
                  >
                    {getStatusTitle()}
                  </Title>
                  
                  <Text 
                    size="md" 
                    ta="center" 
                    c={colorScheme === 'dark' ? 'gray.3' : 'gray.7'}
                  >
                    {getStatusMessage()}
                  </Text>
                </Stack>

                {/* Información del pago */}
                {paymentInfo?.session && (
                  <Card
                    radius="md"
                    p="md"
                    style={{
                      backgroundColor: colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[0],
                      width: '100%'
                    }}
                  >
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">Plan:</Text>
                        <Text size="sm" fw={600}>{paymentInfo.session.planName}</Text>
                      </Group>
                      
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">Monto:</Text>
                        <Text size="sm" fw={600}>${paymentInfo.session.amount}</Text>
                      </Group>

                      {paymentInfo.paymentId && (
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">ID de pago:</Text>
                          <Text size="sm" ff="monospace">{paymentInfo.paymentId}</Text>
                        </Group>
                      )}
                    </Stack>
                  </Card>
                )}

                {/* Botones de acción */}
                <Group justify="center" gap="md" style={{ width: '100%' }}>
                  {processingStatus === 'success' ? (
                    <Button
                      size="lg"
                      rightSection={<IconArrowRight size={16} />}
                      onClick={handleContinue}
                      fullWidth
                    >
                      Ir al dashboard
                    </Button>
                  ) : processingStatus === 'pending' ? (
                    <>
                      <Button
                        variant="light"
                        onClick={handleContinue}
                        style={{ flex: 1 }}
                      >
                        Ver mis suscripciones
                      </Button>
                      <Button
                        onClick={() => navigate('/dashboard')}
                        style={{ flex: 1 }}
                      >
                        Ir al dashboard
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="light"
                        onClick={handleRetry}
                        style={{ flex: 1 }}
                      >
                        Intentar de nuevo
                      </Button>
                      <Button
                        onClick={() => navigate('/dashboard')}
                        style={{ flex: 1 }}
                      >
                        Ir al inicio
                      </Button>
                    </>
                  )}
                </Group>

                {processingStatus === 'success' && (
                  <Text size="xs" ta="center" c="dimmed">
                    Recibirás un email de confirmación en los próximos minutos
                  </Text>
                )}
              </Stack>
            </Card>
          </Center>
        </Container>
      </PageTransition>
    </div>
  );
};

export default PaymentSuccessPage;